/**
 * HOST THE BAKED IMAGES BEFORE THE EMAIL LEAVES THE BROWSER (2026-09-16).
 *
 * Two Studio features produce images that exist only in this tab: the hero slideshow GIF
 * (services/heroGif.ts) and the overlay-text hero (services/overlay.ts). Both sit in the
 * block's image field as `data:` URLs — the canvas draws them, and no email client would.
 * The monolith shipped them with a note ("host it before sending") and no host.
 *
 * At submit, every distinct `data:image/…;base64,…` in the outgoing HTML is uploaded once
 * through `/sfmc-image` (Content Builder image asset, SFMC's own CDN URL) and replaced —
 * in the HTML AND in the block doc, so the visual spec that reaches Airtable carries the
 * hosted URL rather than a megabyte of base64. Failure is reported, not hidden: the caller
 * gets `failed` and decides (the form blocks the submit and says so — an email with a
 * data: hero is a broken hero).
 */
import { hostImage } from "./api";

export const DATA_IMG_RE = /data:image\/(?:gif|jpe?g|png);base64,[A-Za-z0-9+/=]+/g;

export function findDataImages(html: string): string[] {
  const seen = new Set<string>();
  for (const m of String(html || "").match(DATA_IMG_RE) || []) seen.add(m);
  return Array.from(seen);
}

/** replace every occurrence of each key in every string value of a JSON-able doc */
export function swapInDoc<T>(doc: T, map: Map<string, string>): T {
  if (!map.size) return doc;
  let s = JSON.stringify(doc);
  map.forEach((to, from) => { s = s.split(from).join(to); });
  return JSON.parse(s) as T;
}

export interface HostResult<T> { html: string; doc: T; hosted: number; failed: string[]; /** data url → hosted url, for any other string that carries the same image */ map: Map<string, string> }

/**
 * ── AN OVERLAY HERO IS BAKED ONTO JACK'S WORKER; THE EMAIL MUST NOT SHIP IT FROM THERE
 * (2026-10-09, Jack: "host the baked image on SFMC") ─────────────────────────────────────
 * The overlay editor is the old form's code, vendored byte for byte, and its Done hosts the
 * baked JPEG on `intel.lennar-hub.com/hero-img/<id>` (`__ovHost`) so the canvas has a real URL
 * at once. That is right for the preview and wrong for the email: production creative would
 * load its hero from a personal Cloudflare account. So at submit every such URL is treated
 * exactly like a `data:` bake — fetched (the route serves CORS), uploaded once to Content
 * Builder through /sfmc-image, and swapped in the HTML and the doc. The vendor stays verbatim;
 * nothing the editor writes changes, only what leaves the browser.
 */
export const WORKER_BAKE_RE = /https:\/\/intel\.lennar-hub\.com\/hero-img\/[A-Za-z0-9_-]+(?:\.(?:png|jpe?g))?/g;

export function findWorkerBakes(html: string): string[] {
  const seen = new Set<string>();
  for (const m of String(html || "").match(WORKER_BAKE_RE) || []) seen.add(m);
  return Array.from(seen);
}

/** fetch a hosted image and hand it back as a data: URL (what /sfmc-image takes) */
export async function urlToDataUrl(url: string): Promise<string> {
  /* the worker's /hero-img answers CORS for lennar.com origins (and "*" when there is no Origin) */
  const r = await fetch(url, { mode: "cors" });
  if (!r.ok) throw new Error(url + " → " + r.status);
  const blob = await r.blob();
  return await new Promise<string>((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(String(fr.result || ""));
    fr.onerror = () => rej(fr.error || new Error("read failed"));
    fr.readAsDataURL(blob);
  });
}

export async function hostDataImages<T>(html: string, doc: T, name: string, host: (dataUrl: string, name: string) => Promise<{ url: string }> = hostImage, load: (url: string) => Promise<string> = urlToDataUrl): Promise<HostResult<T>> {
  const found = [...findDataImages(html), ...findWorkerBakes(html)];
  const map = new Map<string, string>();
  const failed: string[] = [];
  for (let i = 0; i < found.length; i++) {
    try {
      const src = /^data:/i.test(found[i]) ? found[i] : await load(found[i]);
      const { url } = await host(src, name + (found.length > 1 ? "_" + (i + 1) : ""));
      if (url) map.set(found[i], url); else failed.push(found[i].slice(0, 40));
    } catch (e) {
      failed.push(String((e as Error).message || e));
    }
  }
  let out = html;
  map.forEach((to, from) => { out = out.split(from).join(to); });
  return { html: out, doc: swapInDoc(doc, map), hosted: map.size, failed, map };
}

/** apply the same swaps to any other string (the review pane's baked html, say) */
export function swapInStr(s: string, map: Map<string, string>): string {
  let out = s;
  map.forEach((to, from) => { out = out.split(from).join(to); });
  return out;
}
